import { z } from "zod";

/*
 * Autopilot roles (docs/autopilot.md "Agent roles"; owner's decision 5 of
 * docs/plans/2026-10-20-ai-assistant.md and decision 2 of 2026-10-22 in
 * docs/adr/0002-planes-ownership-packages.md): what may be done on a
 * server besides monitoring. Read access (`agent-ro`: the diagnostic
 * helper and the Unix user `cloudserver-agent`) is the base; on top of it
 * two independent capabilities, in any combination:
 * - `actions` («Действия Автопилота») — a sudoers rule letting that user
 *   run the helper's write commands as root, each after a person's
 *   confirmation;
 * - `apps` («Управление приложениями») — the package verbs
 *   (docs/packages.md): bundles signed by our key, applied through the
 *   change protocol, and the bundle signer's public key on the server.
 * A role name stands for one combination; the names are what the panel
 * stores and what the role file on the server holds:
 *
 * | role           | read | actions | apps |
 * | -------------- | ---- | ------- | ---- |
 * | `monitoring`   |      |         |      |
 * | `agent-ro`     | yes  |         |      |
 * | `agent-rw`     | yes  | yes     |      |
 * | `agent-apps`   | yes  |         | yes  |
 * | `agent-manage` | yes  | yes     | yes  |
 *
 * `agent-manage` meant "agent-rw plus packages" before the split, so the
 * servers that have it keep both capabilities, unchanged.
 */

export const AGENT_ROLES = [
  "monitoring",
  "agent-ro",
  "agent-rw",
  "agent-apps",
  "agent-manage",
] as const;
export const AgentRole = z.enum(AGENT_ROLES);
export type AgentRole = z.infer<typeof AgentRole>;

/** What a role allows: read commands, the write commands («Действия»), the package verbs («Приложения»). */
export interface AgentAccess {
  read: boolean;
  actions: boolean;
  apps: boolean;
}
export type AgentCapability = keyof AgentAccess;

const ACCESS: Record<AgentRole, AgentAccess> = {
  monitoring: { read: false, actions: false, apps: false },
  "agent-ro": { read: true, actions: false, apps: false },
  "agent-rw": { read: true, actions: true, apps: false },
  "agent-apps": { read: true, actions: false, apps: true },
  "agent-manage": { read: true, actions: true, apps: true },
};

/** The capabilities of a role. */
export const roleAccess = (role: AgentRole): AgentAccess => ACCESS[role];

/** Whether a role has one capability. */
export const roleHas = (role: AgentRole, capability: AgentCapability): boolean =>
  ACCESS[role][capability];

/**
 * The role of a combination. Without read access nothing else applies
 * (`monitoring`), whatever the two capabilities say.
 */
export function roleOf(access: AgentAccess): AgentRole {
  if (!access.read) return "monitoring";
  if (access.actions && access.apps) return "agent-manage";
  if (access.actions) return "agent-rw";
  if (access.apps) return "agent-apps";
  return "agent-ro";
}

/** Whether `role` has every capability `needed` has (a package's `requires.autopilotRole`). */
export const roleCovers = (role: AgentRole, needed: AgentRole): boolean =>
  (Object.keys(ACCESS[needed]) as AgentCapability[]).every(
    (capability) => !ACCESS[needed][capability] || ACCESS[role][capability],
  );

/**
 * The helper's package verbs (capability `apps`): run as root through
 * sudo, bundles signed by us. `pkg_op` runs a day-2 operation;
 * `pkg_plan`, `pkg_patch` and `pkg_adopt`
 * handle the customer's files a bundle changes (docs/packages.md,
 * "Customer files").
 */
export const AGENT_PACKAGE_VERBS = [
  "pkg_apply",
  "pkg_remove",
  "pkg_status",
  "pkg_drift",
  "pkg_plan",
  "pkg_patch",
  "pkg_adopt",
  "pkg_op",
] as const;
export type AgentPackageVerb = (typeof AGENT_PACKAGE_VERBS)[number];

/** Read commands of the helper (every role but `monitoring`); arguments are checked by the API and the helper. */
export const AGENT_READ_COMMANDS = [
  "agent_info",
  "uptime",
  "df",
  "free",
  "top_snapshot",
  "ps_list",
  "ss_listen",
  "ip_addr",
  "ip_route",
  "systemctl_status",
  "systemctl_failed",
  "journal",
  "dmesg_tail",
  "os_release",
  "packages_upgradable",
  "firewall_status",
  "docker_ps",
  "docker_logs",
  "file_head",
  "dns_lookup",
  "curl_head",
] as const;

/** Write commands of the helper (`agent-rw`): run as root through sudo, never without a confirmation. */
export const AGENT_WRITE_COMMANDS = [
  "systemctl_restart",
  "systemctl_reload",
  "systemctl_start",
  "systemctl_stop",
  "docker_restart",
  "packages_upgrade_security",
  "package_install",
  "firewall_allow",
  "firewall_deny",
  "nginx_test_reload",
  "logrotate_force",
] as const;

export const AgentReadCommand = z.enum(AGENT_READ_COMMANDS);
export type AgentReadCommand = z.infer<typeof AgentReadCommand>;
export const AgentWriteCommand = z.enum(AGENT_WRITE_COMMANDS);
export type AgentWriteCommand = z.infer<typeof AgentWriteCommand>;
export const AgentCommandId = z.enum([...AGENT_READ_COMMANDS, ...AGENT_WRITE_COMMANDS]);
export type AgentCommandId = z.infer<typeof AgentCommandId>;

/** Arguments by name, e.g. `{unit: "nginx", lines: "200", since: "1h"}`. */
export const AgentCommandArgs = z.record(z.string().max(64), z.string().max(2048));
export type AgentCommandArgs = z.infer<typeof AgentCommandArgs>;

/** What the helper on the server said about itself when last asked (`agent_info`). */
export const AgentHelperState = z.object({
  version: z.string(),
  /** The role installed on the server, which may differ from the one chosen in the panel. */
  role: AgentRole,
  /** Command ids this helper knows. */
  commands: z.array(z.string()),
  checkedAt: z.iso.datetime(),
});
export type AgentHelperState = z.infer<typeof AgentHelperState>;

/** `PUT /projects/:projectId/servers/:serverId/autopilot/role`. */
export const SetAgentRoleRequest = z.object({ role: AgentRole });
export type SetAgentRoleRequest = z.infer<typeof SetAgentRoleRequest>;

/**
 * The role is stored at once; the server follows when the command runs on
 * it as root: `install` adds or refreshes the role's parts, `remove` takes
 * away what the lower role does not have, `none` when nothing is to be done.
 */
export const SetAgentRoleResponse = z.object({
  role: AgentRole,
  action: z.enum(["install", "remove", "none"]),
  command: z.string().nullable(),
});
export type SetAgentRoleResponse = z.infer<typeof SetAgentRoleResponse>;

/** One run of a helper command, after capping and redaction. */
export const AgentExecResult = z.object({
  command: AgentCommandId,
  args: AgentCommandArgs,
  /** The helper's exit status; 64 when it refused the request; null when it gave none. */
  exitCode: z.number().int().nullable(),
  output: z.string(),
  /** The output reached the cap (64 KiB) and was cut. */
  truncated: z.boolean(),
  /** How many secrets were hidden from the output. */
  redacted: z.number().int(),
  durationMs: z.number().int(),
});
export type AgentExecResult = z.infer<typeof AgentExecResult>;

/** `POST …/autopilot/agent/check`: a read command tried from the «Автопилот» tab. */
export const AgentCheckRequest = z.object({
  command: AgentReadCommand.default("agent_info"),
  args: AgentCommandArgs.default({}),
});
export type AgentCheckRequest = z.infer<typeof AgentCheckRequest>;

export const AgentCheckResponse = z.object({
  result: AgentExecResult,
  /** Updated by every `agent_info` that answered. */
  helper: AgentHelperState.nullable(),
});
export type AgentCheckResponse = z.infer<typeof AgentCheckResponse>;
